import { Component, computed, ElementRef, inject, input } from '@angular/core';
import { ActivitySectionDirective, MenusService, UserActivityClickTrackingDirective } from '@core';
import { HeaderComponent, PageHeaderBackSwipeConfig } from '@app/components/header/header.component';
import { rxResource, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { get } from 'lodash';
import { LoadingComponent } from '@app/components/loading/loading.component';
import { TextTranslatePipe } from '@app/pipes/text-translate.pipe';
import { TextComponent } from '@app/components/text/text.component';
import { RouterLink } from '@angular/router';
import { ScrollComponent } from '@app/components/scroll/scroll.component';
import { ImageTranslatePipe } from '@app/pipes/image-translate.pipe';
import { TextTranslatableSchema } from '@app/schemas/text';
import { PageViewPdfMenuConditionSchema, PageViewPdfMenuSchema } from '@app/views/page-pdf-menu/page-pdf-menu.schema';
import { LanguageService } from '@app/services';
import { ColorTranslatePipe } from '@app/pipes/color-translate.pipe';
import { ImageComponent } from '@app/components/image/image.component';
import { RefLinkParamsPipe } from '@app/pipes/ref-link-params.pipe';
import { HeaderBadgeTableNameComponent } from '@app/components/header/header-badge-table-name/header-badge-table-name.component';
import { HeaderBadgeTextComponent } from '@app/components/header/header-badge-text/header-badge-text.component';
import { HeaderTextComponent } from '@app/components/header/header-text/header-text.component';

@Component({
  selector: 'app-page-pdf-menu',
  templateUrl: 'page-pdf-menu.component.html',
  styleUrl: 'page-pdf-menu.component.scss',
  providers: [ColorTranslatePipe],
  imports: [
    ActivitySectionDirective,
    HeaderComponent,
    UserActivityClickTrackingDirective,
    LoadingComponent,
    TextTranslatePipe,
    TextComponent,
    RouterLink,
    ScrollComponent,
    ImageTranslatePipe,
    ColorTranslatePipe,
    ImageComponent,
    RefLinkParamsPipe,
    HeaderBadgeTableNameComponent,
    HeaderBadgeTextComponent,
    HeaderTextComponent,
  ],
  host: {
    class: 'page',
    '[style]': 'styles()',
  },
})
export class PagePdfMenuComponent {

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly languageService = inject(LanguageService);
  private readonly colorTranslatePipe = inject(ColorTranslatePipe);
  private readonly menusService = inject(MenusService);

  protected readonly backSwipeConfig: PageHeaderBackSwipeConfig = {
    direction: 'vertical',
    element: this.elementRef.nativeElement,
  };

  public readonly pageId = input.required<string>();
  public readonly pageName = input.required<TextTranslatableSchema>();
  public readonly pageViewConfig = input.required<PageViewPdfMenuSchema>();

  protected readonly currentLanguage = toSignal(this.languageService.currentLanguage$, {
    requireSync: true,
  });

  protected readonly styles = computed(() => {
    const config = this.pageViewConfig();
    const language = this.currentLanguage();

    return {
      '--base-background-color': this.colorTranslatePipe.transform(config?.theme?.background?.color, language),
      '--base-foreground-color': this.colorTranslatePipe.transform(config?.theme?.foreground?.color, language),
    };
  });

  private readonly filter$ = toObservable(this.pageViewConfig).pipe(
    map(({ filter }) => filter),
  );

  private readonly menus$ = combineLatest([
    this.menusService.menusWithIcon$,
    this.languageService.currentLanguage$,
    this.filter$,
  ]).pipe(
    map(([menus, language, filter]) => {
      if (menus === undefined) {
        return [];
      }

      return menus.filter((menu) => {
        if (menu.hidden) {
          return false;
        }

        return !menu.language || menu.language === language;
      }).filter((menu) => {
        if (!filter) {
          return true;
        }

        return this.condition({
          name: menu.name,
          happyHours: menu.happyHours,
        }, filter);
      });
    }),
  );

  public readonly menusResource = rxResource({
    stream: () => this.menus$,
    defaultValue: [],
  });

  private condition(object: object, condition: PageViewPdfMenuConditionSchema): boolean {
    if (condition.type === 'group') {
      if (condition.conjunction === 'and') {
        return condition.conditions.every((cond) => this.condition(object, cond));
      }

      return condition.conditions.some((cond) => this.condition(object, cond));
    }

    const fieldValue: unknown = get(object, condition.path);
    const value = condition.value;
    const isStringField = typeof fieldValue === 'string' && typeof value === 'string';

    switch (condition.type) {
      case 'eq':
        return fieldValue === value;
      case '!eq':
        return fieldValue !== value;
      case 'contains':
        return isStringField && fieldValue.includes(value);
      case '!contains':
        return isStringField && !fieldValue.includes(value);
      case 'startsWith':
        return isStringField && fieldValue.startsWith(value);
      case '!startsWith':
        return isStringField && !fieldValue.startsWith(value);
      case 'endsWith':
        return isStringField && fieldValue.endsWith(value);
      case '!endsWith':
        return isStringField && !fieldValue.endsWith(value);
      default:
        return false;
    }
  }

}
